/**
 * Referrals page — share link, balance, list of invitees, payout history.
 *
 * Route: /app/referrals
 */

import { motion } from "framer-motion";
import {
  ArrowLeft,
  Banknote,
  Check,
  Copy,
  Crown,
  Send,
  Share2,
  Sparkles,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button, Card, Input, Sheet } from "@/design";
import {
  usePayouts,
  useReferralList,
  useReferralStats,
  useRequestPayout,
  type Payout,
} from "@/api/referrals";
import { cn } from "@/lib/cn";
import { haptic, tg } from "@/lib/telegram";

export function ReferralsPage() {
  const navigate = useNavigate();
  const { data: stats } = useReferralStats();
  const { data: list } = useReferralList({ limit: 30 });
  const { data: payouts } = usePayouts();

  const [payoutOpen, setPayoutOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!stats?.deep_link) return;
    haptic.select();
    try {
      await navigator.clipboard.writeText(stats.deep_link);
      setCopied(true);
      toast.success("Ссылка скопирована");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const handleShare = () => {
    if (!stats?.deep_link) return;
    haptic.impact("medium");
    const text = `🚀 Подключайся к SilentPoster — авто-постинг в Telegram-чаты\n+${stats.reward_pct}% мне с твоих платежей, тебе — стабильная подписка`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(stats.deep_link)}&text=${encodeURIComponent(text)}`;
    if (tg) {
      tg.openTelegramLink(url);
    } else {
      window.open(url, "_blank", "noopener");
    }
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate(-1);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Реферальная программа</div>
        <button
          onClick={handleShare}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Поделиться"
        >
          <Share2 className="size-4" />
        </button>
      </div>

      {/* Hero — balance + reward */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card padded={false} className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-gradient-to-br from-accent/25 via-transparent to-transparent pointer-events-none"
            aria-hidden
          />
          <div className="relative p-6 space-y-5">
            <div className="flex items-center gap-2">
              <div className="size-9 rounded-xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)]">
                <Sparkles className="size-4 text-white" />
              </div>
              <div className="text-xs text-text-muted uppercase tracking-wider font-medium">
                Реферальный баланс
              </div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-[42px] leading-none font-semibold tabular-nums tracking-tight">
                ${(stats?.balance_usd ?? 0).toFixed(2)}
              </span>
              <span className="text-text-muted text-sm">USD</span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <MiniStat
                icon={<Users className="size-3" />}
                label="Приглашено"
                value={stats?.referrals_count ?? 0}
              />
              <MiniStat
                icon={<Crown className="size-3" />}
                label="Платящих"
                value={stats?.paid_referrals_count ?? 0}
              />
              <MiniStat
                icon={<TrendingUp className="size-3" />}
                label="Заработано"
                value={`$${(stats?.total_earned_usd ?? 0).toFixed(0)}`}
              />
            </div>

            <Button
              fullWidth
              variant="primary"
              leftIcon={<Wallet />}
              onClick={() => {
                haptic.select();
                setPayoutOpen(true);
              }}
              disabled={!stats || stats.balance_usd < (stats?.payout_min_usd ?? 5)}
            >
              Запросить выплату
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Share link */}
      <Card padded>
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium mb-2">
          Ваша ссылка
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/[0.05] px-3 py-2.5 text-[13px] font-mono truncate">
          {stats?.deep_link ?? "Загрузка…"}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button
            variant="secondary"
            leftIcon={copied ? <Check /> : <Copy />}
            onClick={handleCopy}
          >
            {copied ? "Скопировано" : "Копировать"}
          </Button>
          <Button leftIcon={<Send />} onClick={handleShare}>
            Поделиться
          </Button>
        </div>
        <div className="mt-3 text-[11px] text-text-muted leading-relaxed">
          С каждой оплаты приглашённого получаете{" "}
          <span className="text-accent font-medium">
            {stats?.reward_pct ?? 30}%
          </span>{" "}
          на баланс. Минимальная сумма к выводу — ${stats?.payout_min_usd ?? 5}.
        </div>
      </Card>

      {/* Referrals list */}
      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 flex items-center gap-1.5">
          <Users className="size-3" />
          Ваши приглашённые
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
          {!list || list.items.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-text-muted">
              Пока никого нет — поделитесь ссылкой
            </div>
          ) : (
            list.items.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-center gap-3">
                <div
                  className={cn(
                    "size-9 rounded-xl grid place-items-center shrink-0 text-[13px] font-semibold",
                    r.has_paid
                      ? "bg-accent-soft text-accent"
                      : "bg-white/[0.04] text-text-dim",
                  )}
                >
                  {(r.first_name ?? r.username ?? "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">
                    {r.first_name ?? r.username ?? `User #${r.id}`}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {r.has_paid ? "Оплатил подписку" : "Зарегистрировался"}
                  </div>
                </div>
                {r.has_paid && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/15 text-success text-[10px] font-medium">
                    <Crown className="size-3" />
                    Pro
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Payouts history */}
      {payouts && payouts.items.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 flex items-center gap-1.5">
            <Banknote className="size-3" />
            Выплаты
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
            {payouts.items.map((p) => (
              <PayoutRow key={p.id} payout={p} />
            ))}
          </div>
        </div>
      )}

      <PayoutSheet
        open={payoutOpen}
        onClose={() => setPayoutOpen(false)}
        maxAmount={stats?.balance_usd ?? 0}
        minAmount={stats?.payout_min_usd ?? 5}
      />
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function MiniStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="flex items-center gap-1 text-text-dim">{icon}</div>
      <div className="text-[10px] text-text-dim uppercase tracking-wider mt-1">{label}</div>
      <div className="text-[15px] font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function PayoutRow({ payout }: { payout: Payout }) {
  const tone =
    payout.status === "paid"
      ? "text-success bg-success/15"
      : payout.status === "rejected"
      ? "text-danger bg-danger/15"
      : "text-text-muted bg-white/[0.05]";
  const label =
    payout.status === "paid"
      ? "Выплачено"
      : payout.status === "rejected"
      ? "Отказано"
      : "В обработке";
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-medium">${payout.amount_usd.toFixed(2)}</div>
          <div className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full", tone)}>
            {label}
          </div>
        </div>
        <div className="text-[11px] text-text-muted mt-0.5">
          {new Date(payout.created_at).toLocaleString("ru-RU", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
}

function PayoutSheet({
  open,
  onClose,
  maxAmount,
  minAmount,
}: {
  open: boolean;
  onClose: () => void;
  maxAmount: number;
  minAmount: number;
}) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const mut = useRequestPayout();

  const parsed = Number(amount.replace(",", "."));
  const valid = !Number.isNaN(parsed) && parsed >= minAmount && parsed <= maxAmount;

  const submit = async () => {
    if (!valid) return;
    try {
      await mut.mutateAsync({ amount_usd: parsed, note: note || null });
      toast.success("Заявка на выплату создана");
      haptic.notify("success");
      setAmount("");
      setNote("");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось создать заявку");
      haptic.notify("error");
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Запросить выплату" description={`Доступно: $${maxAmount.toFixed(2)} · Мин: $${minAmount.toFixed(0)}`}>
      <div className="space-y-4">
        <Input
          label="Сумма (USD)"
          inputMode="decimal"
          placeholder={`${minAmount.toFixed(0)}`}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          rightSlot={
            <button
              className="text-[11px] text-accent font-medium px-2"
              onClick={() => setAmount(maxAmount.toFixed(2))}
            >
              Макс.
            </button>
          }
        />
        <Input
          label="Комментарий (необязательно)"
          placeholder="Например: USDT TRC20, адрес…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          fullWidth
          onClick={submit}
          loading={mut.isPending}
          disabled={!valid}
        >
          Отправить заявку
        </Button>
      </div>
    </Sheet>
  );
}
